import React from 'react';
import { Navigate, NavLink, Route, Routes } from 'react-router-dom';
import { RateCardAdmin } from '@/features/admin/rate-card-admin';
import { DataFreshness } from '@/features/admin/data-freshness';
import { ApiKeysAdmin } from '@/features/admin/api-keys-admin';
import { UnlinkedMedia } from '@/features/admin/unlinked-media';
import { CampaignQa } from '@/features/admin/campaign-qa';
import { useFlagEnabled } from '@/utils/feature-flags';
import { clsx } from 'clsx';

type AdminFlags = {
  rateCard: boolean | undefined;
  apiKeys: boolean | undefined;
  campaignQa: boolean | undefined;
};

export function AdminSection() {
  const adminFlags = useAdminFlags();
  const { rateCard, apiKeys, campaignQa } = adminFlags;
  const defaultPath = getDefaultPath(adminFlags);

  return (
    <div className="flex h-full w-full flex-col bg-background pt-4">
      <nav className="flex shrink-0 gap-4 border-b border-border px-6">
        {rateCard && <AdminNavLink to="/admin/rate-card" label="Rate Card" />}
        <AdminNavLink to="/admin/data-freshness" label="Data Freshness" />
        {apiKeys && <AdminNavLink to="/admin/api-keys" label="API Keys" />}
        <AdminNavLink to="/admin/unlinked-media" label="Unlinked Media" />
        {campaignQa && <AdminNavLink to="/admin/campaign-qa" label="Campaign QA" />}
      </nav>
      <div className="min-h-0 flex-1">
        <Routes>
          {rateCard && (
            <Route
              path="rate-card"
              element={
                <ScrollablePane>
                  <RateCardAdmin />
                </ScrollablePane>
              }
            />
          )}
          <Route
            path="data-freshness"
            element={
              <ScrollablePane>
                <DataFreshness />
              </ScrollablePane>
            }
          />
          {apiKeys && (
            <Route
              path="api-keys"
              element={
                <ScrollablePane>
                  <ApiKeysAdmin />
                </ScrollablePane>
              }
            />
          )}
          <Route
            path="unlinked-media"
            element={
              <ScrollablePane>
                <UnlinkedMedia />
              </ScrollablePane>
            }
          />
          {campaignQa && (
            <Route
              path="campaign-qa"
              element={
                <ScrollablePane>
                  <CampaignQa />
                </ScrollablePane>
              }
            />
          )}
          <Route path="*" element={<Navigate to={defaultPath} replace />} />
        </Routes>
      </div>
    </div>
  );
}

export function useAdminEnabled() {
  return true;
}

function getDefaultPath({ rateCard }: AdminFlags) {
  if (rateCard) return 'rate-card';
  else return 'data-freshness';
}

function useAdminFlags(): AdminFlags {
  const rateCard = useFlagEnabled('admin-rate-card');
  const apiKeys = useFlagEnabled('admin-api-keys');
  const campaignQa = useFlagEnabled('admin-campaign-qa');
  return { rateCard, apiKeys, campaignQa };
}

function ScrollablePane({ children }: { children: React.ReactNode }) {
  return <div className="h-full overflow-y-auto">{children}</div>;
}

function AdminNavLink({ to, label }: { to: string; label: string }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        clsx(
          'border-b-2 px-1 pb-3 text-sm font-medium',
          isActive
            ? 'border-indigo-500 text-gray-900'
            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
        )
      }>
      {label}
    </NavLink>
  );
}
